import { OrderCategory, OrderCostingVersion, OrderStatus, TApiOrder, TApiOrderDetails } from "../api/endpoints/Order";
import { User, UserType } from "../entities/User";
import OrderHelpers from "../utils/OrderHelpers";

export const isOrderValid = (order: TApiOrderDetails) => {
  const { version } = order.cost;

  return !(order.category === OrderCategory.REALTIME && ['v1', 'v2'].includes(version));
}

const isSubmitFrozenForUser = (user: User): boolean => {
  return user.type === UserType.Customer &&
    user.details.freeze.enabled;
}

const isSupportedOrder = (order: TApiOrder): boolean => {
  const { category } = order;

  return category === OrderCategory.REALTIME ||
    category === OrderCategory.ARCHIVE ||
    category === OrderCategory.WEB;
}

const OrderPolicy = {
  canSubmit(user: User, order: TApiOrderDetails, costingVersions: Array<OrderCostingVersion>) {
    const currentCostingVersion = OrderHelpers.currentOrderVersion(order, costingVersions);

    return (
      isSupportedOrder(order) &&
      !OrderHelpers.isExpired(order) &&
      !OrderHelpers.isOverVolumeLimit(order.cost, currentCostingVersion) &&
      user.type === UserType.Customer &&
      !isSubmitFrozenForUser(user) &&
      order.status === OrderStatus.DRAFT
    );
  },

  canApprove(user: User, order: TApiOrder) {
    return (user.type === UserType.CCP || user.type === UserType.Admin) &&
      (order.status === OrderStatus.SUBMITTED || order.status === OrderStatus.ACTION_REQUIRED)
  },

  canReject(user: User, order: TApiOrder) {
    return (user.type === UserType.CCP || user.type === UserType.Admin) &&
      (order.status === OrderStatus.SUBMITTED || order.status === OrderStatus.ACTION_REQUIRED)
  },

  canCancel(user: User, order: TApiOrder) {
    return user.type === UserType.Customer &&
      (order.status === OrderStatus.SUBMITTED || order.status === OrderStatus.ACTION_REQUIRED)
  },

  canRemove(user: User, order: TApiOrder) {
    return user.type !== UserType.Unknown &&
      order.status === OrderStatus.DRAFT
  },

  canEdit(user: User, order: TApiOrderDetails) {
    return (
      isSupportedOrder(order) &&
      !OrderHelpers.isExpired(order) &&
      user.type !== UserType.Unknown &&
      order.status === OrderStatus.DRAFT
    );
  },

  canDuplicate(user: User, order: TApiOrder) {
    return (
      isSupportedOrder(order) &&
      user.type !== UserType.Unknown
    );
  },

  canAddProduct(user: User, order: TApiOrderDetails, costingVersions: Array<OrderCostingVersion>) {
    const currentCostingVersion = OrderHelpers.currentOrderVersion(order, costingVersions);

    return (
      !OrderHelpers.isExpired(order) &&
      !OrderHelpers.isOverVolumeLimit(order.cost, currentCostingVersion) &&
      user.type !== UserType.Unknown &&
      order.category === OrderCategory.REALTIME &&
      order.status === OrderStatus.DRAFT
    );
  },

  canUpdateProduct(user: User, order: TApiOrderDetails) {
    return !OrderHelpers.isExpired(order) &&
      user.type !== UserType.Unknown &&
      order.category === OrderCategory.REALTIME &&
      order.status === OrderStatus.DRAFT  },

  canRemoveProduct(user: User, order: TApiOrderDetails) {
    return !OrderHelpers.isExpired(order) &&
      user.type !== UserType.Unknown &&
      order.category === OrderCategory.REALTIME &&
      order.status === OrderStatus.DRAFT  },

  canSetCostingVersion(user: User, order: TApiOrderDetails) {
    return order.status === OrderStatus.DRAFT;
  },

  canRequestSmallBusinessDiscount(user: User, order: TApiOrder | { category: OrderCategory }) {
    // As of the start of Feb 2026, we don't have any small business discounts
    return false;
  },

  canRequestResearchLicence(user: User, order: TApiOrder | { category: OrderCategory }) {
    return order.category === OrderCategory.REALTIME ||
      order.category === OrderCategory.ARCHIVE;
  },

  canViewCcpOrders(user: User) {
    return user.type === UserType.Admin;
  },

  canViewPublicOrders(user: User) {
    return user.type === UserType.Admin;
  },

  canViewPendingOrders(user: User) {
    return user.type === UserType.CCP;
  },

  canViewInformationCostBreakdown(user: User, orderCategory: OrderCategory) {
    return (user.type === UserType.Admin || user.type === UserType.CCP) &&
      (orderCategory === OrderCategory.REALTIME || orderCategory === OrderCategory.EU_CUTOUT);
  },

  canViewEcQuoteVersion(user: User) {
    return true;
  },

  canViewFullCostError(user: User) {
    return user.type === UserType.Admin;
  },

  canViewServiceChargeEstimation(user: User, order: TApiOrder) {
    return (user.type === UserType.Admin || user.type === UserType.CCP) && order.status === OrderStatus.DRAFT;
  }
}

export default OrderPolicy;
